import {
  Divider,
  Tooltip as MuiTooltip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SlaTargetIcon } from "components/icons/dual-tone/SlaTargetIcon";
import { SlaTimerIcon } from "components/icons/dual-tone/SlaTimerIcon";
import SlaDetailsCard from "pages/modules/service-desk/tickets/components/ticket-details/components/ticket-header/SlaDetailsCard";
import { memo } from "react";
import type { SlaTrackingData, SlaTrackingStatus } from "shared";

interface SlaCountdownBadgeProps {
  /** Full tracking data for badge + tooltip */
  trackingData?: SlaTrackingData | null;
  ticketStatus?: string;
}

// const formatRemainingTime = (remainingMinutes: number): string => {
//   if (remainingMinutes <= 0) return "0m";
//   const hours = Math.floor(remainingMinutes / 60);
//   const mins = Math.round(remainingMinutes % 60);
//   if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
//   if (hours > 0) return `${hours}h`;
//   return `${mins}m`;
// };

const formatRemainingTime = (remainingMinutes: number): string => {
  const isNegative = remainingMinutes < 0;
  const prefix = isNegative ? "-" : "";
  const abs = Math.ceil(Math.abs(remainingMinutes));
  if (abs === 0) return "0m";
  const days = Math.floor(abs / 1440);
  const hours = Math.floor((abs % 1440) / 60);
  const mins = abs % 60;
  if (days > 0 && hours > 0) return `${prefix}${days}d ${hours}h`;
  if (days > 0) return `${prefix}${days}d`;
  if (hours > 0 && mins > 0) return `${prefix}${hours}h ${mins}m`;
  if (hours > 0) return `${prefix}${hours}h`;
  return `${prefix}${mins}m`;
};

// --- Main Badge ---

export const SlaCountdownBadge = memo(
  ({ trackingData, ticketStatus }: SlaCountdownBadgeProps) => {
    const theme = useTheme();

    const remainingFirstResponse =
      trackingData?.firstResponseTargetMinutes != null &&
      trackingData?.firstResponseElapsedMinutes != null
        ? formatRemainingTime(
            trackingData.firstResponseTargetMinutes -
              trackingData.firstResponseElapsedMinutes,
          )
        : "--";

    const remainingResolution =
      trackingData?.resolutionTargetMinutes != null &&
      trackingData?.resolutionElapsedMinutes != null
        ? formatRemainingTime(
            trackingData.resolutionTargetMinutes -
              trackingData.resolutionElapsedMinutes,
          )
        : "--";

    const getStatusColors = (
      slaStatus: SlaTrackingStatus | undefined,
    ): { strokeColor: string; fillColor: string } => {
      const sys = theme.palette.primitive.system;
      switch (slaStatus) {
        case "on_track":
          return {
            strokeColor: sys.green[800] ?? "",
            fillColor: sys.green[100] ?? "",
          };
        case "met":
          return {
            strokeColor: sys.blue[800] ?? "",
            fillColor: sys.blue[200] ?? "",
          };
        case "at_risk":
          return {
            strokeColor: sys.orange[800] ?? "",
            fillColor: sys.orange[100] ?? "",
          };
        case "breached":
          return {
            strokeColor: sys.red[800] ?? "",
            fillColor: sys.red[100] ?? "",
          };
        case "paused":
          return {
            strokeColor: sys.yellow[800] ?? "",
            fillColor: sys.yellow[100] ?? "",
          };
        case "pending":
          return {
            strokeColor: sys.violet[700] ?? "",
            fillColor: sys.violet[200] ?? "",
          };
        default:
          return {
            strokeColor: sys.green[800] ?? "",
            fillColor: sys.green[100] ?? "",
          };
      }
    };

    const isFirstResponseDisabled =
      trackingData?.firstResponseStatus === "disabled";
    const isResolutionDisabled = trackingData?.resolutionStatus === "disabled";

    if (isFirstResponseDisabled && isResolutionDisabled) return null;

    const firstResponseColors = getStatusColors(
      trackingData?.firstResponseStatus,
    );
    const resolutionColors = getStatusColors(trackingData?.resolutionStatus);

    const showBoth = !isFirstResponseDisabled && !isResolutionDisabled;

    const badge = (
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        sx={{
          padding: "4px 12px 4px 8px",
          borderRadius: "4px",
          minWidth: "84px",
          border: `1px solid ${theme.palette.neutral["015"]}`,
        }}
        gap="4px"
      >
        {!isFirstResponseDisabled && (
          <Stack direction="row" alignItems="center" gap="4px">
            <SlaTimerIcon
              primaryColor={firstResponseColors.strokeColor}
              secondaryColor={firstResponseColors.fillColor}
              fontSize="small"
            />
            <Typography
              variant="body-small"
              sx={{
                color:
                  trackingData?.firstResponseStatus === "met" ||
                  trackingData?.firstResponseStatus === "breached"
                    ? undefined
                    : firstResponseColors.strokeColor,
              }}
            >
              {trackingData?.firstResponseStatus === "met"
                ? "Met"
                : trackingData?.firstResponseStatus === "breached"
                  ? "Breached"
                  : remainingFirstResponse || "--"}
            </Typography>
          </Stack>
        )}
        {showBoth && <Divider orientation="vertical" flexItem />}
        {!isResolutionDisabled && (
          <Stack direction="row" alignItems="center" gap="4px">
            <SlaTargetIcon
              primaryColor={resolutionColors.strokeColor}
              secondaryColor={resolutionColors.fillColor}
              fontSize="small"
            />
            <Typography
              variant="body-small"
              sx={{
                color:
                  trackingData?.resolutionStatus === "met" ||
                  trackingData?.resolutionStatus === "breached"
                    ? undefined
                    : resolutionColors.strokeColor,
              }}
            >
              {trackingData?.resolutionStatus === "met"
                ? "Met"
                : trackingData?.resolutionStatus === "breached"
                  ? "Breached"
                  : remainingResolution || "--"}
            </Typography>
          </Stack>
        )}
      </Stack>
    );

    if (!trackingData) return badge;

    return (
      <MuiTooltip
        title={
          <SlaDetailsCard
            trackingData={trackingData}
            ticketStatus={ticketStatus}
          />
        }
        placement="bottom-start"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              padding: 0,
              maxWidth: "none",
            },
          },
        }}
      >
        {badge}
      </MuiTooltip>
    );
  },
);

SlaCountdownBadge.displayName = "SlaCountdownBadge";
